import { memo, useEffect, useMemo } from 'react';
import * as THREE from 'three';
import { Line, Text } from '@react-three/drei';
import { useAppStore } from '../state/store';
import { useLayoutStore, type CadLayout } from '../state/useLayoutStore';

/**
 * Paper-space layout renderer (docs/plans/2d plan Faz 9, option A):
 * the 2D workspace's layout view — a paper sheet with floating
 * MVIEW viewports over the model.
 *
 * Technique: model content is drawn at z≈0 (scaled per viewport); the paper
 * is an opaque white Shape mesh with rectangular holes at the viewport
 * frames (or VPCLIP polygons), so content shows ONLY through the holes —
 * real viewport clipping without stencil plumbing. Entities tagged
 * `onSheet = layout.id` are the paper's own annotations (TEXT/HATCH drawn
 * at paper coordinates above the sheet).
 */

const PAPER_COLOR = '#ffffff';
const PAPER_EDGE = '#222222';
const VP_FRAME = '#3a6ea5';
const TITLE_COLOR = '#111111';

/** Model-space entity render (paper-space copy of the drafting subset). */
function PaperEntity({
    e,
}: {
    e: { type: string; data: Record<string, any> };
}) {
    // The sheet is always white paper, so the default ink here is always
    // black. Entities created before theming carry a legacy '#ffffff'
    // default (the dark-viewport ink) which would vanish on paper — treat
    // it as unset; explicitly chosen colors render as configured.
    const paperInk = (c: string | undefined): string =>
        !c || c === '#ffffff' ? '#000000' : c;
    const color = paperInk(e.data.color as string);
    switch (e.type) {
        case 'line': {
            const s = e.data.start as number[], t = e.data.end as number[];
            if (!Array.isArray(s) || !Array.isArray(t)) return null;
            return <Line points={[[s[0], s[1], 0], [t[0], t[1], 0]]} color={color} lineWidth={1.2} />;
        }
        case 'polyline':
        case 'polygon': {
            const vs = (e.data.vertices as number[][]) ?? [];
            if (vs.length < 2) return null;
            return (
                <Line
                    points={vs.map((v) => [v[0], v[1], 0] as [number, number, number])}
                    color={color}
                    lineWidth={1.2}
                    dashed={Array.isArray(e.data.dash)}
                    segments
                />
            );
        }
        case 'circle':
        case 'arc': {
            const c = e.data.center as number[];
            const r = e.data.radius as number;
            if (!Array.isArray(c) || !Number.isFinite(r)) return null;
            let a0 = 0, a1 = Math.PI * 2;
            if (e.type === 'arc') { a0 = e.data.startAngle ?? 0; a1 = e.data.endAngle ?? Math.PI * 2; }
            let sweep = a1 - a0;
            while (sweep <= 0) sweep += Math.PI * 2;
            const pts: [number, number, number][] = [];
            const n = Math.max(8, Math.round(sweep / (Math.PI / 24)));
            for (let i = 0; i <= n; i++) {
                const a = a0 + (sweep * i) / n;
                pts.push([c[0] + Math.cos(a) * r, c[1] + Math.sin(a) * r, 0]);
            }
            return <Line points={pts} color={color} lineWidth={1.2} />;
        }
        case 'text':
        case 'mtext':
            return (
                <Text
                    position={[e.data.position?.[0] ?? 0, e.data.position?.[1] ?? 0, 0]}
                    fontSize={0.02}
                    color={color}
                    anchorX="left"
                    anchorY="bottom"
                >
                    {String(e.data.content ?? '')}
                </Text>
            );
        case 'point': {
            const p = e.data.position as number[];
            if (!Array.isArray(p)) return null;
            return (
                <Line points={[[p[0] - 0.02, p[1], 0], [p[0] + 0.02, p[1], 0], [p[0], p[1] - 0.02, 0], [p[0], p[1] + 0.02, 0]]}
                    segments color={color} lineWidth={1} />
            );
        }
        default:
            return null;
    }
}

function ViewportGroup({ layout }: { layout: CadLayout }) {
    const entities = useAppStore((s) => s.entities);
    const layers = useAppStore((s) => s.layers);
    const model = useMemo(() => Object.values(entities).filter((e) =>
        e.visible && !e.data.onSheet
        && !(e.type === 'wall' || e.type === 'door' || e.type === 'window' || e.type === 'floor' || e.type === 'roof' || e.type === 'ceiling' || e.type === 'stair' || e.type === 'railing' || e.type === 'ramp' || e.type === 'component' || e.type === 'area' || e.type === 'toposolid')
    ), [entities]);

    return (
        <>
            {layout.viewports.map((vp) => {
                const s = vp.scale > 0 ? vp.scale : 1;
                // VPLAYER frozen set holds layer NAMES (the VPLAYER command /
                // Layer Manager convention); accept raw ids too so hand-edited
                // documents keep working.
                const hidden = new Set(vp.frozenLayers);
                const visible = model.filter((e) =>
                    !hidden.has(e.layer) && !hidden.has(layers[e.layer]?.name ?? ''));
                return (
                    <group key={vp.id}>
                        {/* Model content, scaled around the view center. */}
                        <group position={[vp.cx - vp.viewCx * s, vp.cy - vp.viewCy * s, -0.002]} scale={[s, s, 1]}>
                            {visible.map((e) => <PaperEntity key={e.id} e={e} />)}
                        </group>
                        {/* Frame */}
                        <Line
                            points={[
                                [vp.cx - vp.w / 2, vp.cy - vp.h / 2, 0.004],
                                [vp.cx + vp.w / 2, vp.cy - vp.h / 2, 0.004],
                                [vp.cx + vp.w / 2, vp.cy + vp.h / 2, 0.004],
                                [vp.cx - vp.w / 2, vp.cy + vp.h / 2, 0.004],
                                [vp.cx - vp.w / 2, vp.cy - vp.h / 2, 0.004],
                            ]}
                            color={VP_FRAME}
                            lineWidth={1.5}
                        />
                        {vp.showTitle && (
                            <Text
                                position={[vp.cx, vp.cy - vp.h / 2 - 0.02, 0.004]}
                                fontSize={0.018}
                                color={TITLE_COLOR}
                                anchorX="center"
                                anchorY="top"
                            >
                                {`${vp.title || 'View'} (${vp.scale}:1)`}
                            </Text>
                        )}
                    </group>
                );
            })}
        </>
    );
}

export const PaperSpaceCadLayer = memo(function PaperSpaceCadLayer() {
    const activeId = useLayoutStore((s) => s.activeId);
    const layouts = useLayoutStore((s) => s.layouts);
    const entities = useAppStore((s) => s.entities);
    const layout = layouts.find((l) => l.id === activeId) ?? null;
    const hw = (layout?.width ?? 0.42) / 2, hh = (layout?.height ?? 0.297) / 2;
    // Paper sheet with viewport holes (VPCLIP polygon wins over the frame).
    const sheetGeom = useMemo(() => {
        const shape = new THREE.Shape();
        shape.moveTo(-hw, -hh);
        shape.lineTo(hw, -hh);
        shape.lineTo(hw, hh);
        shape.lineTo(-hw, hh);
        shape.closePath();
        layout?.viewports.forEach((vp) => {
            const hole = new THREE.Path();
            if (vp.clip && vp.clip.length >= 3) {
                vp.clip.forEach((p, i) => (i === 0 ? hole.moveTo(p[0], p[1]) : hole.lineTo(p[0], p[1])));
                hole.closePath();
            } else {
                hole.moveTo(vp.cx - vp.w / 2, vp.cy - vp.h / 2);
                hole.lineTo(vp.cx + vp.w / 2, vp.cy - vp.h / 2);
                hole.lineTo(vp.cx + vp.w / 2, vp.cy + vp.h / 2);
                hole.lineTo(vp.cx - vp.w / 2, vp.cy + vp.h / 2);
                hole.closePath();
            }
            shape.holes.push(hole);
        });
        return new THREE.ShapeGeometry(shape);
    }, [hw, hh, layout]);

    if (!layout) return null;

    // Frame the sheet when the layout changes: fit the paper region.
    useEffect(() => {
        window.dispatchEvent(new CustomEvent('yap:zoom-region', {
            detail: {
                a: [-layout.width / 2, -layout.height / 2, 0],
                b: [layout.width / 2, layout.height / 2, 0],
            },
        }));
    }, [layout.id, layout.width, layout.height]);

    const paperEntities = Object.values(entities).filter((e) => e.visible && e.data.onSheet === layout.id);

    return (
        <group>
            {/* Model content through the viewport holes */}
            <ViewportGroup layout={layout} />
            {/* The opaque paper itself (with holes) */}
            <mesh geometry={sheetGeom} position={[0, 0, 0]} renderOrder={1}>
                <meshBasicMaterial color={PAPER_COLOR} depthWrite />
            </mesh>
            {/* Sheet edge */}
            <Line
                points={[[-hw, -hh, 0.003], [hw, -hh, 0.003], [hw, hh, 0.003], [-hw, hh, 0.003], [-hw, -hh, 0.003]]}
                color={PAPER_EDGE}
                lineWidth={2}
            />
            {/* Layout tab label */}
            <Text position={[0, -hh - 0.03, 0.003]} fontSize={0.03} color="#cfe3ff" anchorX="center" anchorY="top">
                {layout.name}
            </Text>
            {/* Paper-space annotations owned by this layout */}
            <group position={[0, 0, 0.004]}>
                {paperEntities.map((e) => <PaperEntity key={e.id} e={e} />)}
            </group>
        </group>
    );
});
